import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

export interface ActiveContributor {
  id: string;
  display_name: string;
  title: string | null;
  avatar_url: string | null;
  username: string | null;
  channels: string[];
  kb_count: number;
}

export const useActiveContributors = (channelName?: string) => {
  return useQuery({
    queryKey: ['active-contributors', channelName],
    queryFn: async () => {
      if (!channelName) return [];

      // Get all published knowledge bases for this channel with creator info
      let kbQuery = supabase
        .from('knowledge_bases')
        .select('creator_id, profiles!inner(id, display_name, title, avatar_url, username)')
        .eq('status', 'published')
        .is('deleted_at', null);
      
      // For ispai channel, show all published KBs
      // For other channels, filter by channel_name
      if (channelName.toLowerCase() !== 'ispai') {
        kbQuery = kbQuery.eq('channel_name', channelName.toUpperCase());
      }

      const { data: kbData, error: kbError } = await kbQuery;
      
      if (kbError) {
        console.error('Error fetching knowledge bases:', kbError);
        return [];
      }

      if (!kbData || kbData.length === 0) return [];

      // Group by creator and count KBs
      const creatorMap = new Map<string, {
        profile: any;
        count: number;
      }>();

      kbData.forEach((kb) => {
        const creatorId = kb.creator_id;
        const profile = kb.profiles;
        
        if (creatorMap.has(creatorId)) {
          creatorMap.get(creatorId)!.count++;
        } else {
          creatorMap.set(creatorId, { profile, count: 1 });
        }
      });

      // Get all channels for all creators
      const creatorIds = Array.from(creatorMap.keys());
      const { data: orgData } = await supabase
        .from('user_organizations')
        .select('user_id, organization_name, channels(name)')
        .in('user_id', creatorIds);

      // Build the final result
      const contributors: ActiveContributor[] = Array.from(creatorMap.entries()).map(([creatorId, { profile, count }]) => {
        const userOrgs = orgData?.filter(org => org.user_id === creatorId) || [];
        const channels = userOrgs
          .map(org => org.channels?.name || org.organization_name)
          .filter((name): name is string => name !== null && name !== undefined);
        
        return {
          id: profile.id,
          display_name: profile.display_name,
          title: profile.title,
          avatar_url: profile.avatar_url,
          username: profile.username,
          channels,
          kb_count: count
        };
      });

      // Sort by KB count descending
      contributors.sort((a, b) => b.kb_count - a.kb_count);

      return contributors;
    },
    enabled: !!channelName,
    staleTime: 0,
    gcTime: 0,
    refetchOnMount: true,
    refetchOnWindowFocus: true
  });
};
